import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'

interface Crumb {
    label: string
    href?: string
}

interface PageHeaderProps {
    breadcrumbs: Crumb[]
    title: ReactNode
    intro?: ReactNode
    meta?: ReactNode
}

/**
 * The navy title band at the top of every inner page, with breadcrumbs.
 */
export const PageHeader: FC<PageHeaderProps> = ({ breadcrumbs, title, intro, meta }): ReactNode => {
    return (
        <div className='relative isolate overflow-hidden bg-navy text-white'>
            <div aria-hidden='true' className='pattern-dots absolute inset-0 -z-10' />
            <div aria-hidden='true' className='absolute -right-24 -top-40 -z-10 size-[26rem] rounded-full bg-gold/15 blur-3xl' />

            <div className='mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20'>
                <nav aria-label='Breadcrumb'>
                    <ol className='flex flex-wrap items-center gap-1.5 text-sm text-white/70'>
                        {breadcrumbs.map((crumb, index) => {
                            const last = index === breadcrumbs.length - 1

                            return (
                                <li key={`${crumb.label}-${index}`} className='flex items-center gap-1.5'>
                                    {crumb.href ? (
                                        <Link href={crumb.href} className='rounded-sm underline-offset-4 hover:text-white hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'>
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span aria-current={last ? 'page' : undefined} className='text-white'>
                                            {crumb.label}
                                        </span>
                                    )}

                                    {!last && <ChevronRight aria-hidden='true' className='size-4 text-gold' />}
                                </li>
                            )
                        })}
                    </ol>
                </nav>

                {meta && (
                    <div className='mt-8 flex flex-wrap items-center gap-3 text-sm'>
                        {meta}
                    </div>
                )}

                <h1 className='mt-5 max-w-4xl text-balance font-heading text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl'>
                    {title}
                </h1>

                {intro && (
                    <div className='mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl'>
                        {intro}
                    </div>
                )}
            </div>
        </div>
    )
}
